import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Lock, Eye, EyeOff, CheckCircle, Check, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { ApiHit } from '../../../Api/ApiHit'
import { resetPassword } from '../../../Constant/Constant'
import { STRONG_PASSWORD_REGEX } from '../../../Constant/Regex'

function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  const [errors, setErrors] = useState({})
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  useEffect(() => {
    // Check if token exists
    if (!token) {
      toast.error('Invalid or missing reset token')
      navigate('/login')
    }
  }, [token, navigate])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      })
    }
  }

  const getPasswordStrength = (password) => {
    return {
      hasLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // New password validation
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required'
    } else if (!STRONG_PASSWORD_REGEX.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must be at least 8 characters with uppercase, lowercase, number, and special character'
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    // Prepare payload for reset password API
    const payload = {
      token: token,
      newPassword: formData.newPassword
    }

    console.log('Reset password payload:', payload)

    // Show loading toast
    const loadingToast = toast.loading('Resetting password...')

    ApiHit(payload, resetPassword).then((res) => {
      console.log("res----", res)
      toast.dismiss(loadingToast)

      if (res?.success) {
        setIsSubmitted(true)
        toast.success('Password reset successful! Redirecting to login...')
        console.log("Password reset successful!")

        // Navigate to login after a delay
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        // Handle error response
        toast.error(res?.error || res?.message || 'Failed to reset password. The link may have expired.')
      }
    }).catch((error) => {
      console.error("Reset password error:", error)
      toast.dismiss(loadingToast)
      toast.error('An error occurred. Please try again or request a new reset link.')
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={require('../../../Images/logo.png')}
            alt="HeadsUp Corporation"
            className="h-16 mx-auto mb-4 object-contain"
          />
          <h2 className="text-2xl font-bold text-gray-800">Reset Your Password</h2>
          <p className="text-gray-600 mt-2">
            {isSubmitted
              ? "Your password has been reset successfully"
              : "Enter your new password below"
            }
          </p>
        </div>

        {/* Reset Password Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {/* New Password Field */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.newPassword
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                )}

                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <div className="mt-2 space-y-1">
                    {(() => {
                      const strength = getPasswordStrength(formData.newPassword)
                      const IndicatorItem = ({ met, text }) => (
                        <div className="flex items-center text-xs">
                          {met ? (
                            <Check className="w-3 h-3 text-green-600 mr-1" />
                          ) : (
                            <X className="w-3 h-3 text-red-400 mr-1" />
                          )}
                          <span className={met ? 'text-green-700' : 'text-gray-500'}>{text}</span>
                        </div>
                      )
                      return (
                        <>
                          <IndicatorItem met={strength.hasLength} text="At least 8 characters" />
                          <IndicatorItem met={strength.hasUppercase} text="One uppercase letter" />
                          <IndicatorItem met={strength.hasLowercase} text="One lowercase letter" />
                          <IndicatorItem met={strength.hasNumber} text="One number" />
                          <IndicatorItem met={strength.hasSpecial} text="One special character (@$!%*?&)" />
                        </>
                      )
                    })()}
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.confirmPassword
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Reset Password
              </button>

              {/* Back to Login Link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Password Reset Complete!</h3>
              <p className="text-gray-600 mb-4">
                Your password has been successfully reset.
              </p>
              <p className="text-sm text-gray-500">
                Redirecting to login page...
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>&copy; 2025 HeadsUp Corporation. All rights reserved.</p>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
